// Discount codes: the codes an organisation defines and staff enter at checkout (F-POS-02; spec/06
// §8.4, spec/13 §4). Part of the `commerce` module (it prices a code against a sale), but organisation
// -scoped, because a code is unique per organisation and applies across its venues.
//
//   GET  …/organizations/{o}/discount-codes    the organisation's codes                discounts:read
//   POST …/organizations/{o}/discount-codes    define a code                           discounts:manage
//   PATCH …/organizations/{o}/discount-codes/{id}  switch a code off or back on        discounts:manage
//
// A code is `percent` (basis points off) or `amount` (a fixed money amount off), with an optional
// maximum discount, minimum order, validity window and usage limits (total and per player). The
// server reserves a code's usage when a sale that carries it is placed (a conditional increment) and
// prices it — the console never computes a discount. Promotions and coupons (F-POS-03, F-POS-06) are
// the same `discounts` group and join it with their features; `fixed_price`, `free_item` and
// `bonus_time` code types ride in with promotions.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { DiscountCodeIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { BasisPointsSchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { DISCOUNTS_MANAGE, DISCOUNTS_READ } from "../permissions/index.js";
/** A discount code's value type (spec/06 §8.4). `percent` and `amount` discount a cart's money. */
export const DiscountCodeTypeSchema = z
    .enum(['percent', 'amount'])
    .openapi('DiscountCodeType', { description: 'How a discount code takes value off a sale (spec/06 §8.4).' });
/** The code itself: 4–24 chars, `A-Z 0-9 -`, stored upper-case, unique per organisation (spec/06 §8.4). */
export const DiscountCodeStringSchema = z
    .string()
    .min(4)
    .max(24)
    .regex(/^[A-Za-z0-9-]+$/, 'A code is A–Z, 0–9 and hyphens.')
    .openapi('DiscountCodeString');
/** A discount code (spec/06 §8.4, F-POS-02). */
export const DiscountCodeSchema = z
    .object({
    id: DiscountCodeIdSchema,
    code: z.string().min(4).max(24),
    type: DiscountCodeTypeSchema,
    /** For `percent`: basis points off (1000 = 10%); null for an amount code. */
    percentBps: BasisPointsSchema.nullable(),
    /** For `amount`: the money off; null for a percent code. */
    amount: NonNegativeMoneySchema.nullable(),
    /** The most a percent code may take off one sale; null for no ceiling. */
    maxDiscount: NonNegativeMoneySchema.nullable(),
    /** The least a sale must total before the code applies; null for none. */
    minOrder: NonNegativeMoneySchema.nullable(),
    /** The total times the code may be used; null for unlimited. */
    usageLimit: z.int().min(1).nullable(),
    /** How many times it has been used, reserved when a sale is placed (spec/06 §8.4 rule 3). */
    usageCount: z.int().min(0),
    /** The times one player may use it; null for no per-player limit. */
    perPlayerLimit: z.int().min(1).nullable(),
    active: z.boolean(),
    validFrom: IsoDateTimeSchema.nullable(),
    validUntil: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('DiscountCode');
export const DiscountCodeListSchema = z.object({ codes: z.array(DiscountCodeSchema) }).openapi('DiscountCodeList');
/** Define a discount code (spec/06 §8.4). Exactly one of `percentBps` / `amount` per the `type`. */
export const CreateDiscountCodeSchema = z
    .object({
    code: DiscountCodeStringSchema,
    type: DiscountCodeTypeSchema,
    percentBps: BasisPointsSchema.optional(),
    amount: NonNegativeMoneySchema.optional(),
    maxDiscount: NonNegativeMoneySchema.optional(),
    minOrder: NonNegativeMoneySchema.optional(),
    usageLimit: z.int().min(1).max(1_000_000).optional(),
    perPlayerLimit: z.int().min(1).max(1000).optional(),
    validFrom: IsoDateTimeSchema.optional(),
    validUntil: IsoDateTimeSchema.optional(),
})
    .openapi('CreateDiscountCodeRequest');
export const listDiscountCodesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/discount-codes',
    operationId: 'listDiscountCodes',
    tags: ['commerce'],
    summary: 'List discount codes (F-POS-02)',
    [PERMISSION_EXTENSION]: DISCOUNTS_READ,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(DiscountCodeListSchema, 'The organisation’s discount codes, newest first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createDiscountCodeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/discount-codes',
    operationId: 'createDiscountCode',
    tags: ['commerce'],
    summary: 'Define a discount code (F-POS-02)',
    description: 'Creates an organisation discount code staff can enter at checkout. `percent` needs `percentBps`, ' +
        '`amount` needs `amount`. Requires `discounts:manage`. Audited as configuration.',
    [PERMISSION_EXTENSION]: DISCOUNTS_MANAGE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreateDiscountCodeSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(DiscountCodeSchema, 'The created code.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        409: problemResponse('A code with that value already exists in this organisation.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** Switch a discount code off (or back on). A deactivated code is refused at checkout (spec/06 §8.4). */
export const UpdateDiscountCodeSchema = z
    .object({ active: z.boolean() })
    .strict()
    .openapi('UpdateDiscountCodeRequest');
const DiscountCodeParamsSchema = OrganizationScopeParamsSchema.extend({ discountCodeId: DiscountCodeIdSchema }).openapi('DiscountCodeParams');
export const updateDiscountCodeRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/discount-codes/{discountCodeId}',
    operationId: 'updateDiscountCode',
    tags: ['commerce'],
    summary: 'Switch a discount code off or on (F-POS-02)',
    description: 'Deactivates (or reactivates) an organisation discount code. A deactivated code is refused at ' +
        'checkout; its usage history stays. Requires `discounts:manage`. Audited as configuration.',
    [PERMISSION_EXTENSION]: DISCOUNTS_MANAGE,
    request: {
        params: DiscountCodeParamsSchema,
        body: { content: { 'application/json': { schema: UpdateDiscountCodeSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(DiscountCodeSchema, 'The updated code.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such discount code in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=discounts.js.map